import React, { useState } from "react";
import {
  Archive,
  Calendar,
  CheckCircle,
  Clock,
  Image as ImageIcon,
  RotateCcw,
} from "lucide-react";
import { ImageLightbox } from "../../../components/ui/ImageLightbox";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import { Button } from "../../../components/ui/button";
import { SurfaceCard } from "../../../components/ui/SurfaceCard";
import { AppLink } from "../../../components/ui/link";
import { ssrFormattedDateOr } from "../../../lib/ssrFormattedDate";
import type { MainAppPageProps, Quote, UpcomingVisit } from "../types";

type QuotesPageProps = Pick<
  MainAppPageProps,
  "csrfToken" | "routes" | "quotes" | "unreadQuoteCount" | "upcomingVisits"
> & {
  /** Notifies shell (sidenav/header badge) when a quote is marked read. */
  onQuoteMarkedRead?: () => void;
};

const QuoteCard: React.FC<{
  quote: Quote;
  csrfToken: string;
  markReadUrl: string;
  archiveUrl: string;
  restoreUrl: string;
  onMarkRead: (id: number) => void;
  onStatusChanged: (quote: Quote) => void;
  onBookingConfirmed: (quoteId: number, booking: Quote["booking"]) => void;
}> = ({
  quote,
  csrfToken,
  markReadUrl,
  archiveUrl,
  restoreUrl,
  onMarkRead,
  onStatusChanged,
  onBookingConfirmed,
}) => {
  const [isMarking, setIsMarking] = useState(false);
  const [isUpdatingArchive, setIsUpdatingArchive] = useState(false);
  const [confirmingTime, setConfirmingTime] = useState<string | null>(null);
  const [photoLightboxIndex, setPhotoLightboxIndex] = useState<number | null>(
    null,
  );

  const handleMarkRead = async () => {
    if (quote.status !== "new") return;

    setIsMarking(true);
    try {
      const url = markReadUrl.replace(":id", String(quote.id));
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });

      if (response.ok) {
        onMarkRead(quote.id);
      }
    } catch (error) {
      console.error("Failed to mark quote as read:", error);
    } finally {
      setIsMarking(false);
    }
  };

  const handleArchiveStatus = async () => {
    setIsUpdatingArchive(true);
    try {
      const template = quote.status === "archived" ? restoreUrl : archiveUrl;
      const response = await fetch(template.replace(":id", String(quote.id)), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });
      if (response.ok) {
        const data = (await response.json()) as { quote: Quote };
        onStatusChanged(data.quote);
      }
    } catch (error) {
      console.error("Failed to update quote archive status:", error);
    } finally {
      setIsUpdatingArchive(false);
    }
  };

  const handleConfirmTime = async (preferredTime: string) => {
    setConfirmingTime(preferredTime);
    try {
      const response = await fetch(`/dashboard/quotes/${quote.id}/bookings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ preferred_time: preferredTime }),
      });

      if (response.ok) {
        const data = await response.json();
        onBookingConfirmed(quote.id, data.booking);
      } else {
        const data = await response.json();
        alert(data.error || "Failed to confirm booking");
      }
    } catch (error) {
      console.error("Failed to confirm time:", error);
      alert("Failed to confirm booking");
    } finally {
      setConfirmingTime(null);
    }
  };

  return (
    <SurfaceCard
      className={quote.status === "new" ? "border-l-4 border-l-accent" : ""}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="mb-2 flex items-center gap-2">
            <h3 className="font-semibold text-foreground">{quote.name}</h3>
            {quote.status === "new" && (
              <span className="rounded-full bg-accent/15 px-2 py-0.5 text-xs font-medium text-accent">
                New
              </span>
            )}
            {quote.status === "archived" && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                Archived
              </span>
            )}
            {quote.booking?.status === "confirmed" && (
              <span className="flex items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 text-xs font-medium text-success">
                <CheckCircle className="h-3 w-3" />
                Visit Confirmed
              </span>
            )}
          </div>
          <div className="mb-3 space-y-1 text-sm text-muted-foreground">
            <p>
              <span className="font-medium">Email:</span>{" "}
              <a
                href={`mailto:${quote.email}`}
                className="text-premium-600 hover:underline"
              >
                {quote.email}
              </a>
            </p>
            {quote.phone && (
              <p>
                <span className="font-medium">Phone:</span>{" "}
                <a
                  href={`tel:${quote.phone}`}
                  className="text-premium-600 hover:underline"
                >
                  {quote.phone}
                </a>
              </p>
            )}
            {quote.postalCode && (
              <p>
                <span className="font-medium">Postal Code:</span>{" "}
                {quote.postalCode}
              </p>
            )}
            <p>
              <span className="font-medium">Service:</span> {quote.service}
            </p>
          </div>
          <div className="rounded-lg bg-muted/35 p-3">
            <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
              Message
            </p>
            <p className="whitespace-pre-wrap text-sm text-foreground">
              {quote.message}
            </p>
          </div>

          {quote.photos && quote.photos.length > 0 ? (
            <div className="mt-4 rounded-lg bg-muted/35 p-3">
              <p className="mb-2 flex items-center gap-1 text-xs font-medium uppercase text-muted-foreground">
                <ImageIcon className="h-3 w-3" />
                Project photos ({quote.photos.length})
              </p>
              <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {quote.photos.map((photo, index) => (
                  <li key={photo.id}>
                    <button
                      type="button"
                      className="block w-full overflow-hidden rounded-md border border-border/70 focus:outline-none focus:ring-2 focus:ring-ring/20"
                      onClick={() => setPhotoLightboxIndex(index)}
                    >
                      <img
                        src={photo.url}
                        alt={`Project photo ${index + 1}`}
                        className="aspect-square w-full object-cover"
                      />
                    </button>
                  </li>
                ))}
              </ul>
              {photoLightboxIndex !== null ? (
                <ImageLightbox
                  images={quote.photos.map((photo, index) => ({
                    src: photo.url,
                    alt: `Project photo ${index + 1}`,
                  }))}
                  currentIndex={photoLightboxIndex}
                  onClose={() => setPhotoLightboxIndex(null)}
                  onNavigate={setPhotoLightboxIndex}
                />
              ) : null}
            </div>
          ) : null}

          {/* Time Preferences Section */}
          {quote.timePreferences &&
            quote.timePreferences.length > 0 &&
            !quote.booking &&
            quote.status !== "archived" && (
              <div className="mt-4 rounded-lg bg-accent/10 p-3">
                <p className="mb-2 flex items-center gap-1 text-xs font-medium uppercase text-accent">
                  <Calendar className="h-3 w-3" />
                  Preferred Visit Times
                </p>
                <div className="space-y-2">
                  {quote.timePreferences.map((pref) => (
                    <div
                      key={pref.id}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm text-foreground">
                        <span className="font-medium text-accent">
                          #{pref.preferenceOrder}
                        </span>{" "}
                        {pref.display}
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => handleConfirmTime(pref.preferredTime)}
                        disabled={confirmingTime !== null}
                        className="rounded bg-success px-3 py-1 text-xs font-medium text-success-foreground hover:brightness-95 disabled:opacity-50"
                      >
                        {confirmingTime === pref.preferredTime
                          ? "..."
                          : "Confirm"}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Confirmed Booking Info */}
          {quote.booking?.status === "confirmed" && (
            <div className="mt-4 rounded-lg bg-success/10 p-3">
              <p className="mb-1 flex items-center gap-1 text-xs font-medium uppercase text-success">
                <CheckCircle className="h-3 w-3" />
                Confirmed Visit
              </p>
              <p className="text-sm font-medium text-foreground">
                {quote.booking.display}
              </p>
            </div>
          )}

          <p className="mt-3 text-xs text-muted-foreground">
            {ssrFormattedDateOr(quote.createdAtDisplay)}
          </p>
        </div>
        <div className="ml-4 flex shrink-0 flex-col gap-2">
          {quote.status === "new" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleMarkRead}
              disabled={isMarking}
            >
              {isMarking ? "..." : "Mark Read"}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleArchiveStatus}
            disabled={isUpdatingArchive}
          >
            {quote.status === "archived" ? (
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
            ) : (
              <Archive className="mr-1 h-3.5 w-3.5" />
            )}
            {isUpdatingArchive
              ? "..."
              : quote.status === "archived"
                ? "Restore"
                : "Archive"}
          </Button>
        </div>
      </div>
    </SurfaceCard>
  );
};

const UpcomingVisitsCard: React.FC<{ visits: UpcomingVisit[] }> = ({
  visits,
}) => {
  if (visits.length === 0) return null;

  return (
    <SurfaceCard className="mb-6">
      <div className="mb-4 flex items-center gap-2">
        <Clock className="h-5 w-5 text-accent" />
        <h2 className="text-lg font-semibold text-foreground">
          Upcoming Quote Visits
        </h2>
      </div>
      <div className="space-y-3">
        {visits.map((visit) => (
          <div
            key={visit.id}
            className="flex items-center justify-between rounded-lg border border-border bg-muted/35 p-3"
          >
            <div>
              <p className="font-medium text-foreground">
                {visit.customerName}
              </p>
              <p className="text-sm text-muted-foreground">{visit.service}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                <a
                  href={`mailto:${visit.customerEmail}`}
                  className="hover:underline"
                >
                  {visit.customerEmail}
                </a>
                {visit.customerPhone && (
                  <>
                    {" · "}
                    <a
                      href={`tel:${visit.customerPhone}`}
                      className="hover:underline"
                    >
                      {visit.customerPhone}
                    </a>
                  </>
                )}
              </p>
            </div>
            <div className="text-right">
              <p className="font-medium text-accent">{visit.display}</p>
            </div>
          </div>
        ))}
      </div>
    </SurfaceCard>
  );
};

const QuotesPage: React.FC<QuotesPageProps> = ({
  csrfToken,
  routes,
  quotes = [],
  unreadQuoteCount = 0,
  upcomingVisits = [],
  onQuoteMarkedRead,
}) => {
  const [localQuotes, setLocalQuotes] = useState<Quote[]>(quotes);
  const [localUnreadCount, setLocalUnreadCount] = useState(unreadQuoteCount);
  const [localUpcomingVisits, setLocalUpcomingVisits] =
    useState<UpcomingVisit[]>(upcomingVisits);
  const [showArchived, setShowArchived] = useState(false);

  const handleMarkRead = (quoteId: number) => {
    setLocalQuotes((prev) =>
      prev.map((q) =>
        q.id === quoteId
          ? { ...q, status: "read" as const, readAt: new Date().toISOString() }
          : q,
      ),
    );
    setLocalUnreadCount((prev) => Math.max(0, prev - 1));
    onQuoteMarkedRead?.();
  };

  const handleStatusChanged = (updatedQuote: Quote) => {
    const previous = localQuotes.find((quote) => quote.id === updatedQuote.id);
    setLocalQuotes((current) =>
      current.map((quote) =>
        quote.id === updatedQuote.id ? updatedQuote : quote,
      ),
    );
    if (previous?.status === "new" && updatedQuote.status === "archived") {
      setLocalUnreadCount((current) => Math.max(0, current - 1));
      onQuoteMarkedRead?.();
    }
  };

  const handleBookingConfirmed = (
    quoteId: number,
    booking: Quote["booking"],
  ) => {
    setLocalQuotes((prev) =>
      prev.map((q) => (q.id === quoteId ? { ...q, booking } : q)),
    );

    // Add to upcoming visits if confirmed
    if (booking?.status === "confirmed") {
      const quote = localQuotes.find((q) => q.id === quoteId);
      if (quote) {
        const newVisit: UpcomingVisit = {
          id: booking.id,
          startTime: booking.startTime,
          endTime: booking.endTime,
          customerName: quote.name,
          customerEmail: quote.email,
          customerPhone: quote.phone,
          service: quote.service,
          display: booking.display,
        };
        setLocalUpcomingVisits((prev) =>
          [...prev, newVisit].sort((a, b) =>
            a.startTime.localeCompare(b.startTime),
          ),
        );
      }
    }
  };

  const inboxQuotes = localQuotes.filter(
    (quote) => quote.status !== "archived",
  );
  const archivedQuotes = localQuotes.filter(
    (quote) => quote.status === "archived",
  );
  const visibleQuotes = showArchived ? archivedQuotes : inboxQuotes;

  return (
    <DashboardPageLayout
      title="Quotes"
      subtitle={
        localUnreadCount > 0
          ? `${localUnreadCount} new quote${localUnreadCount !== 1 ? "s" : ""}`
          : undefined
      }
      headerActions={
        <div className="flex gap-2">
          <Button
            type="button"
            variant={showArchived ? "outline" : "secondary"}
            onClick={() => setShowArchived(false)}
          >
            Inbox
          </Button>
          <Button
            type="button"
            variant={showArchived ? "secondary" : "outline"}
            onClick={() => setShowArchived(true)}
          >
            Archived ({archivedQuotes.length})
          </Button>
        </div>
      }
    >
      {showArchived ? null : (
        <UpcomingVisitsCard visits={localUpcomingVisits} />
      )}

      {visibleQuotes.length === 0 ? (
        <SurfaceCard>
          <div className="py-12 text-center">
            <p className="text-lg text-muted-foreground">
              {showArchived ? "No archived quotes" : "No quotes yet"}
            </p>
          </div>
        </SurfaceCard>
      ) : (
        <div className="space-y-4">
          {visibleQuotes.map((quote) => (
            <QuoteCard
              key={quote.id}
              quote={quote}
              csrfToken={csrfToken}
              markReadUrl={routes.markQuoteRead}
              archiveUrl={
                routes.archiveQuote ?? "/dashboard/quotes/:id/archive"
              }
              restoreUrl={
                routes.restoreQuote ?? "/dashboard/quotes/:id/restore"
              }
              onMarkRead={handleMarkRead}
              onStatusChanged={handleStatusChanged}
              onBookingConfirmed={handleBookingConfirmed}
            />
          ))}
        </div>
      )}
    </DashboardPageLayout>
  );
};

export default QuotesPage;
